package br.com.vvrefeicoes.portal.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavHostController
import androidx.navigation.toRoute
import br.com.vvrefeicoes.portal.core.di.rememberSessionPermissions
import kotlinx.browser.window
import org.w3c.dom.events.Event

private const val HASH_PREFIX = "#/"
private const val GUIDE_CREATE_ROUTE = "guias-e-manuais/novo"
private const val GUIDE_DETAIL_ROUTE = "guias-e-manuais/{id}"
private const val GUIDE_EDIT_ROUTE = "guias-e-manuais/{id}/editar"
private const val FORM_CREATE_ROUTE = "formularios/criar"
private const val FORM_DETAIL_ROUTE = "formularios/{id}"
private const val FORM_EDIT_ROUTE = "formularios/editar/{id}"

@Composable
actual fun SyncNavigationWithUrl(navController: NavHostController) {
    val allowedKeys = rememberSessionPermissions().keys

    fun navigateToRoute(
        routeValue: String,
        popFirst: Boolean,
    ) {
        val route = routeValue.removeDuplicatedLastSegment()
        val editId = route.guideEditId()
        val detailId = route.guideDetailId()
        val publicMonitorCode = route.publicMonitorCode()
        val formEditId = route.formEditId()
        val formDetailId = route.formDetailId()
        when {
            route == GUIDE_CREATE_ROUTE -> {
                if (canAccessRouteName(allowedKeys, GUIDE_CREATE_ROUTE)) {
                    navController.navigate(GuideCreateRoute) { launchSingleTop = true }
                }
            }
            editId != null -> {
                if (canAccessRouteName(allowedKeys, GUIDE_EDIT_ROUTE)) {
                    navController.navigate(GuideEditRoute(editId)) { launchSingleTop = true }
                }
            }
            detailId != null -> {
                if (canAccessRouteName(allowedKeys, GUIDE_DETAIL_ROUTE)) {
                    navController.navigate(GuideDetailRoute(detailId)) { launchSingleTop = true }
                }
            }
            formEditId != null -> {
                if (canAccessRouteName(allowedKeys, FORM_EDIT_ROUTE)) {
                    navController.navigate(FormularioEditRoute(formEditId)) { launchSingleTop = true }
                }
            }
            formDetailId != null -> {
                if (canAccessRouteName(allowedKeys, FORM_DETAIL_ROUTE)) {
                    navController.navigate(FormularioViewRoute(formDetailId)) { launchSingleTop = true }
                }
            }
            publicMonitorCode != null -> {
                navController.navigate(PublicMonitorRoute(publicMonitorCode)) { launchSingleTop = true }
            }
            canAccessRouteName(allowedKeys, route) &&
                route != navController.currentDestination?.route &&
                navController.graph.findNode(route) != null -> {
                if (popFirst && navController.popBackStack(route, inclusive = false)) {
                    return
                }
                navController.navigate(route) { launchSingleTop = true }
            }
        }
    }

    LaunchedEffect(navController, allowedKeys) {
        currentRouteFromUrl()?.let { route -> navigateToRoute(route, popFirst = false) }
    }

    LaunchedEffect(navController) {
        navController.currentBackStackEntryFlow.collect { entry ->
            val hash = HASH_PREFIX + entry.concreteRoute()
            if (window.location.hash != hash) {
                window.location.hash = hash
            }
        }
    }

    DisposableEffect(navController, allowedKeys) {
        val listener: (Event) -> Unit = {
            currentRouteFromUrl()?.let { route -> navigateToRoute(route, popFirst = true) }
        }
        window.addEventListener("hashchange", listener)
        onDispose { window.removeEventListener("hashchange", listener) }
    }
}

private fun currentRouteFromUrl(): String? =
    window.location.hash
        .removePrefix(HASH_PREFIX)
        .removeDuplicatedLastSegment()
        .takeIf { it.isNotEmpty() }

private fun String.removeDuplicatedLastSegment(): String {
    val parts = split('/')
    return if (parts.size > 1 && parts.last() == parts[parts.lastIndex - 1]) {
        parts.dropLast(1).joinToString("/")
    } else {
        this
    }
}

private fun String.publicMonitorCode(): String? =
    removePrefix("monitor_pesquisa/")
        .takeIf { length == 16 && matches(Regex("[0-9a-fA-F]{16}")) }

private fun String.guideDetailId(): String? =
    if (startsWith("guias-e-manuais/") && !endsWith("/editar") && this != GUIDE_CREATE_ROUTE) {
        removePrefix("guias-e-manuais/").takeIf { it.isNotBlank() && '/' !in it }
    } else {
        null
    }

private fun String.guideEditId(): String? =
    if (startsWith("guias-e-manuais/") && endsWith("/editar")) {
        removePrefix("guias-e-manuais/").removeSuffix("/editar").takeIf { it.isNotBlank() && '/' !in it }
    } else {
        null
    }

private fun String.formEditId(): String? =
    removePrefix("formularios/editar/")
        .takeIf {
            this != FORM_EDIT_ROUTE &&
                startsWith("formularios/editar/") &&
                it.isNotBlank() &&
                '/' !in it
        }

private fun String.formDetailId(): String? =
    removePrefix("formularios/")
        .takeIf {
            this != FORM_CREATE_ROUTE &&
                this != FORM_DETAIL_ROUTE &&
                startsWith("formularios/") &&
                !startsWith("formularios/editar/") &&
                it.isNotBlank() &&
                '/' !in it
        }

private fun NavBackStackEntry.concreteRoute(): String {
    val route = destination.route.orEmpty()
    return when {
        route == GUIDE_CREATE_ROUTE -> route
        route == GUIDE_EDIT_ROUTE -> {
            val id = runCatching { toRoute<GuideEditRoute>().id }.getOrDefault("")
            "guias-e-manuais/$id/editar"
        }
        route.startsWith("guias-e-manuais/") -> {
            val id = runCatching { toRoute<GuideDetailRoute>().id }.getOrDefault("")
            "guias-e-manuais/$id"
        }
        route.startsWith("formularios/editar/") -> {
            val id = runCatching { toRoute<FormularioEditRoute>().id }.getOrDefault("")
            "formularios/editar/$id".removeDuplicatedLastSegment()
        }
        route.startsWith("formularios/") && route != FORM_CREATE_ROUTE -> {
            val id = runCatching { toRoute<FormularioViewRoute>().id }.getOrDefault("")
            "formularios/$id".removeDuplicatedLastSegment()
        }
        else -> route
    }
}
